import { getCustomSentryExceptionError, handleApiErrorWithCode } from "@/utils/error";
import { redirectToLogin } from "@/utils/center";
import { refreshCenterOAuthToken } from "@/features/auth/services/centerOAuthApi";
import { removeToken, setToken, verifyToken } from "@/utils/token";
import * as Sentry from "@sentry/react";
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import Cookies from "js-cookie";

if (!process.env.REACT_APP_API_HOST) {
  throw new Error("please provide REACT_APP_API_HOST in env");
}

export const baseURL = `${process.env.REACT_APP_API_HOST}/admin`;

export const globalBaseURL = `${process.env.REACT_APP_API_HOST}/global`;

export const globalApi = axios.create({
  baseURL: globalBaseURL,
});

export const clientApi = axios.create({
  baseURL,
});

// If successful (do nothing)
export const onSuccess = (response: AxiosResponse) => {
  Sentry.endSession();
  return response;
};

let isRefreshing = false;
let refreshQueue: { resolve: (value: unknown) => void; reject: (reason?: unknown) => void; config: AxiosRequestConfig }[] = [];

const processQueue = (error: unknown, token: string | null = null) => {
  refreshQueue.forEach(({ resolve, reject, config }) => {
    if (token) {
      resolve(
        axios({
          ...config,
          headers: { ...config.headers, Authorization: `Bearer ${token}` },
        }),
      );
    } else {
      reject(error);
    }
  });
  refreshQueue = [];
};

//  in case of error
export const onError = async (error: AxiosError) => {
  if (error.response) {
    // Access Token was expired
    if (error.response.status === 401 && error.config) {
      // Skip refresh for the refresh-token endpoint itself to avoid infinite loop
      if (error.config.url?.includes("/refresh-token")) {
        return Promise.reject(error.response?.data);
      }

      if (isRefreshing) {
        // Queue request while refresh is in progress
        return new Promise((resolve, reject) => {
          refreshQueue.push({ resolve, reject, config: error.config! });
        });
      }

      isRefreshing = true;

      try {
        const accessToken = Cookies.get("admin-token");
        const refreshToken = Cookies.get("admin-refresh-token");
        if (!refreshToken) {
          processQueue(error);
          logout();
          redirectToLogin();
          return Promise.reject(error.response?.data);
        }

        // Admin sessions come from irohana-center (SSO handoff), so tokens belong to
        // center's Cognito pool and must be refreshed at center (separate axios
        // instance, no interceptors) — not against this account's Cognito.
        const data = await refreshCenterOAuthToken(accessToken || "", refreshToken);
        // Center may not return a new refreshToken — keep the existing one
        setToken(data.accessToken, data.refreshToken || refreshToken);

        const newToken = data.accessToken;
        processQueue(null, newToken);

        // Retry original request with new token
        return axios({
          ...error.config,
          headers: { ...error.config.headers, Authorization: `Bearer ${newToken}` },
        });
      } catch (refreshError) {
        processQueue(refreshError);
        logout();
        redirectToLogin();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
  }
  handleApiErrorWithCode(error);
  const status = error.response?.status;
  const shouldCaptureServerError = !!status && status >= 500 && status !== 503;

  if (shouldCaptureServerError) {
    Sentry.captureException(getCustomSentryExceptionError(error));
  }
  Sentry.endSession();

  return Promise.reject(error.response?.data);
};

clientApi.interceptors.request.use(
  (config) => {
    Sentry.startSession();
    return config;
  },
  (error) => {
    if (error.response?.status && error.response.status >= 500 && error.response.status !== 503) {
      Sentry.captureException(getCustomSentryExceptionError(error));
    }
    Sentry.endSession();
    return Promise.reject(error);
  },
);

globalApi.interceptors.request.use(
  (config) => {
    Sentry.startSession();
    return config;
  },
  (error) => {
    if (error.response?.status && error.response.status >= 500 && error.response.status !== 503) {
      Sentry.captureException(getCustomSentryExceptionError(error));
    }
    Sentry.endSession();
    return Promise.reject(error);
  },
);

clientApi.interceptors.response.use(onSuccess, onError);
globalApi.interceptors.response.use(onSuccess, onError);

export const setAxiosAuth = () => {
  const token = Cookies.get("admin-token");
  clientApi.defaults.headers.common.Authorization = `Bearer ${token}`;
  globalApi.defaults.headers.common.Authorization = `Bearer ${token}`;
};

export const removeAxiosAuth = () => {
  clientApi.defaults.headers.common.Authorization = "";
  globalApi.defaults.headers.common.Authorization = "";
};

export const isAuth = () => {
  if (typeof window !== "undefined") {
    const token = Cookies.get("admin-token");
    const isAuthenticated = !!token && verifyToken(token);
    return isAuthenticated;
  }
  return false;
};

export const logout = () => {
  removeToken();
  Cookies.remove("admin-user-id");
  Cookies.remove("admin-email");
  Cookies.remove("contract-token");
};
